import Konva from 'konva';
import { ScalingType } from 'types/Scene';
import { ratioFormats } from 'constants/ratioFormats';
import { RatioFormatType } from 'types/RatioFormat';
import { BASE_SIZE } from 'constants/canvas';
import { Effect } from 'types/Effects';
import textSceneFunc from 'utils/konva/textSceneFunc';
import effectToKonvaTextSettings from 'utils/konva/effectToKonvaTextSettings';
import { clone } from 'ramda';

export const getTextWidth = (fontFamily: string, fontSize: number, value: string) => {
  const text = new Konva.Text({
    fontFamily,
    fontSize
  });
  return text.measureSize([value]).width;
};

export const getLines = (_effect: Effect, fontFamily?: string): string[] => {
  const effect = clone(_effect);
  if (fontFamily) {
    effect.font.fontFamily = fontFamily;
  }
  const konvaText = new Konva.Text(effectToKonvaTextSettings(effect));
  konvaText._sceneFunc = textSceneFunc;
  konvaText.setAttr('text', effect.transformedText || effect.text);

  if (konvaText.textArr.length === 0) {
    return getLines({ ...effect, width: effect.width + 1 }, fontFamily);
  }
  return konvaText.textArr.map(({ text }) => text);
};

export const getTextMaxWidth = (textArr: string[], fontFamily: string, fontSize: number): number =>
  textArr.reduce((acc: number, item: string) => {
    const width = getTextWidth(fontFamily, fontSize, item);
    return width >= acc ? width : acc;
  }, 0);

export const getKonvaTextSizeByEffect = (effect: Effect) => {
  const text = new Konva.Text(effectToKonvaTextSettings(effect));
  return text.measureSize(effect.text);
};

export const getSize = (
  scaling: ScalingType,
  canvasWidth: number,
  canvasHeight: number,
  width: number,
  height: number
): [number, number, number] => {
  const widthRatio = canvasWidth / width;
  const heightRatio = canvasHeight / height;

  if (scaling === ScalingType.FIT) {
    if (widthRatio <= heightRatio) return [canvasWidth, height * widthRatio, widthRatio];
    if (widthRatio > heightRatio) return [width * heightRatio, canvasHeight, heightRatio];
  }

  if (scaling === ScalingType.FILL) {
    if (widthRatio <= heightRatio) return [width * heightRatio, canvasHeight, heightRatio];
    if (widthRatio > heightRatio) return [canvasWidth, height * widthRatio, widthRatio];
  }

  return [0, 0, 1];
};

export const getScale = (
  scaling: ScalingType,
  canvasWidth: number,
  canvasHeight: number,
  width?: number,
  height?: number
): number => {
  if (!width && !height) {
    width = (canvasWidth / canvasHeight) * BASE_SIZE;
    height = BASE_SIZE;
  }

  const widthRatio = canvasWidth / width;
  const heightRatio = canvasHeight / height;

  if (scaling === ScalingType.FIT) {
    if (widthRatio <= heightRatio) return widthRatio;
    if (widthRatio > heightRatio) return heightRatio;
  }

  if (scaling === ScalingType.FILL) {
    if (widthRatio <= heightRatio) return heightRatio;
    if (widthRatio > heightRatio) return widthRatio;
  }

  return 1;
};

export const getScaleChangeFit = (
  oldRatioFormat: RatioFormatType,
  ratioFormat: RatioFormatType
): number => {
  const rf = ratioFormats[ratioFormat].ratio;
  const rfOld = ratioFormats[oldRatioFormat].ratio;
  return rf / rfOld;
};
